import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import ProjectMemberList from './ProjectMemberList';
import MonthSummaryBar from './MonthSummaryBar';
import dayjs from 'dayjs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import {
  Checkbox,
  IconButton,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';


const PROJECT_TYPES = [
  { value: 'operation', label: '運用' },
  { value: 'development', label: '開発' },
  { value: 'production', label: '制作' },
  { value: 'internal', label: '社内' },
  { value: 'other', label: 'その他' },
];

const PROBABILITIES = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];

const STATUSES = [
  { value: 'NOT_STARTED', label: '未着手' },
  { value: 'IN_PROGRESS', label: '進行中' },
  { value: 'INVOICE_REQUESTED', label: '請求書申請中' },
  { value: 'APPROVED', label: '承認済' },
];

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [memberProjectId, setMemberProjectId] = useState(null);

  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');

  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState(null);
  const [copyMode, setCopyMode] = useState('this'); // this | next | custom
  const [customMonth, setCustomMonth] = useState('');
  const [copyMembers, setCopyMembers] = useState(true);
  const [copyPayments, setCopyPayments] = useState(true);
  const [copyContractMinutes, setCopyContractMinutes] = useState(true);
  const [copying, setCopying] = useState(false);
  const [copyResult, setCopyResult] = useState(null); // { severity, message }


  const [showEndMonth, setShowEndMonth] = useState(false);

  const [form, setForm] = useState({
    title: '',
    customerId: '',
    agencyCustomerId: '',
    projectType: 'development',
    recurringType: '',
    probability: 'C',
    status: 'NOT_STARTED',
    leaderUserId: '',
    progress: 0,
    description: '',
    startDate: '',
    endDate: '',
    totalCost: '',
    contractMinutes: '',
  });

  const navigate = useNavigate();

  const loadProjects = async (opts = {}) => {
    try {
      const s = opts.startMonth ?? startMonth;
      const e = opts.endMonth ?? endMonth;

      const params = {};
      if (s) params.startMonth = s;
      if (e) params.endMonth = e;

      const res = await api.get('/api/projects', { params });
      setProjects(res.data || []);
    } catch (err) {
      console.error('[loadProjects error]', err);
    }
  };

  const loadMasters = async () => {
    const [customerRes, userRes] = await Promise.all([
      api.get('/api/customers'),
      api.get('/api/users'),
    ]);
    setCustomers(customerRes.data || []);
    setUsers(userRes.data || []);
  };

  useEffect(() => {
    loadMasters();
    loadProjects();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    const payload = {
      title: form.title,
      customerId: Number(form.customerId),
      agencyCustomerId: form.agencyCustomerId ? Number(form.agencyCustomerId) : null,
      projectType: form.projectType,
      recurringType: form.projectType === 'operation' && form.recurringType ? form.recurringType : null,
      probability: form.probability,
      status: form.status,
      leaderUserId: Number(form.leaderUserId),
      progress: Number(form.progress),
      description: form.description || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      totalCost: form.totalCost ? Number(form.totalCost) : null,
      contractMinutes: form.contractMinutes ? Number(form.contractMinutes) : null,
    };

    if (editId) {
      await api.put(`/api/projects/${editId}`, payload);
    } else {
      await api.post('/api/projects', payload);
    }

    setOpen(false);
    resetForm();
    loadProjects();
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      title: row.title,
      customerId: row.customerId,
      agencyCustomerId: row.agencyCustomerId || '',
      projectType: row.projectType || 'development',
      recurringType: row.recurringType || '',
      probability: row.probability || 'C',
      status: row.status || 'NOT_STARTED',
      leaderUserId: row.leaderUserId,
      progress: row.progress ?? 0,
      description: row.description || '',
      startDate: row.startDate ? row.startDate.substring(0, 10) : '',
      endDate: row.endDate ? row.endDate.substring(0, 10) : '',
      totalCost: row.totalCost ?? '',
      contractMinutes: row.contractMinutes ?? '',
    });

    const cust = customers.find(c => String(c.id) === String(row.customerId));
    const lead = users.find(u => String(u.id) === String(row.leaderUserId));
    setSelectedCustomer(cust || null);
    setSelectedLeader(lead || null);

    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    await api.delete(`/api/projects/${id}`);
    loadProjects();
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      title: '',
      customerId: '',
      agencyCustomerId: '',
      projectType: 'development',
      recurringType: '',
      probability: 'C',
      status: 'NOT_STARTED',
      leaderUserId: '',
      progress: 0,
      description: '',
      startDate: '',
      endDate: '',
      totalCost: '',
      contractMinutes: '',
    });
    setSelectedCustomer(null);
    setSelectedLeader(null);
  };

  // 終点を設定していないとき (showEndMonth=false) は単月表示
  // = endMonth は内部で startMonth と同じ値を使う
  const handleSearch = () => {
    const effectiveEnd = showEndMonth ? endMonth : startMonth;
    loadProjects({ startMonth, endMonth: effectiveEnd });
  };

  const handleClearFilter = () => {
    setStartMonth('');
    setEndMonth('');
    setShowEndMonth(false);
    loadProjects({ startMonth: null, endMonth: null });
  };

  // クイックボタン (先月/今月/来月): 起点=終点=その月で即座に絞り込み
  const applyQuickMonth = (date) => {
    const ym = date.format('YYYY-MM');
    setStartMonth(ym);
    setEndMonth(ym);
    setShowEndMonth(false);
    loadProjects({ startMonth: ym, endMonth: ym });
  };

  const handleCopyStart = (row) => {
    setCopySourceId(row.id);
    setCopyMode('this');
    setCustomMonth('');
    setCopyOpen(true);
  };

  const handleCopyExecute = async () => {
    const src = projects.find(p => p.id === copySourceId);
    if (!src) {
      alert('コピー元の案件が見つかりません');
      return;
    }

    // 開始日が未設定の案件は当月基準でコピーする
    const srcYYYYMM = src.startDate
      ? String(src.startDate).slice(0, 7).replace('-', '')
      : dayjs().format('YYYYMM');
    let targetYYYYMM = '';

    if (copyMode === 'this') {
      targetYYYYMM = srcYYYYMM;
    } else if (copyMode === 'next') {
      const y = Number(srcYYYYMM.slice(0, 4));
      const m = Number(srcYYYYMM.slice(4, 6));
      targetYYYYMM =
        m === 12 ? `${y + 1}01` : `${y}${String(m + 1).padStart(2, '0')}`;
    } else {
      if (!customMonth) {
        alert('年月を選択してください');
        return;
      }
      targetYYYYMM = customMonth.replace('-', '');
    }

    // コピー結果とエラーを必ず画面に返す (xlsx 行88)
    setCopying(true);
    try {
      const { data } = await api.post(`/api/projects/${copySourceId}/copy`, {
        targetMonth: targetYYYYMM,
        copyMembers,
        copyPayments,
        copyContractMinutes,
        resetStatus: true,
      });

      setCopyOpen(false);
      await loadProjects();

      const month = `${targetYYYYMM.slice(0, 4)}/${targetYYYYMM.slice(4, 6)}`;
      setCopyResult({
        severity: 'success',
        message:
          `${month} 分としてコピーしました（案件ID: ${data?.copiedProject?.id ?? '-'}）／` +
          `メンバー: ${data?.copiedMembers ? 'コピー' : '引き継がない'}／` +
          `外注費: ${data?.copiedPayments ? 'コピー（承認待ちに戻す）' : '引き継がない'}／` +
          `契約工数: ${data?.copiedContractMinutes ? 'コピー' : 'リセット'}`,
      });
    } catch (e) {
      console.error('[案件コピーエラー]', e);
      setCopyResult({
        severity: 'error',
        message: `案件のコピーに失敗しました: ${e.response?.data?.error || e.message}`,
      });
    } finally {
      setCopying(false);
    }
  };


  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },

    {
      field: 'customerAndProject',
      headerName: '顧客 / 案件',
      flex: 1.6,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            lineHeight: 1.2,
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {params.row.customerName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {params.row.title}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'leaderName',
      headerName: '担当者',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },

    {
      field: 'statusLabel',
      headerName: 'ステータス',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },

    {
      field: 'progress',
      headerName: '進捗(%)',
      width: 110,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value != null ? `${params.value}%` : ''}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'period',
      headerName: '期間',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            lineHeight: 1.2,
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {params.row.startDate ? String(params.row.startDate).slice(0, 10) : '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {params.row.endDate ? String(params.row.endDate).slice(0, 10) : '-'}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'totalCost',
      headerName: '金額',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="bold">
            {params.value != null ? `¥${Number(params.value).toLocaleString()}` : '-'}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'contractMinutes',
      headerName: '契約工数(分)',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value != null ? params.value : '-'}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'actions',
      headerName: '操作',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: 0.5,
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyItems: 'center',
            padding: 1,
          }}
        >
          {/* 上段 */}
          <Tooltip title="編集">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="メンバー">
            <IconButton
              size="small"
              onClick={() => setMemberProjectId(params.row.id)}
            >
              <GroupIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="詳細">
            <IconButton
              size="small"
              onClick={() => navigate(`/projects/${params.row.id}`)}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 下段（左詰め＋右端） */}
          <Tooltip title="コピー">
            <IconButton
              size="small"
              onClick={() => handleCopyStart(params.row)}
              sx={{ gridColumn: '1 / 2' }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="削除">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
              sx={{ gridColumn: '3 / 4' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }
    ,
  ];

  const baseMonth = dayjs().format("YYYY-MM");

  const agencyValue =
    customers.find(c => String(c.id) === String(form.agencyCustomerId)) || null;

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button size="small" variant="outlined" onClick={() => applyQuickMonth(dayjs().subtract(1, 'month'))}>先月</Button>
        <Button size="small" variant="outlined" onClick={() => applyQuickMonth(dayjs())}>今月</Button>
        <Button size="small" variant="outlined" onClick={() => applyQuickMonth(dayjs().add(1, 'month'))}>来月</Button>
        <TextField
          label="起点月"
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          size="small"
          sx={{ minWidth: 150 }}
        />
        {showEndMonth ? (
          <TextField
            label="終点月"
            type="month"
            value={endMonth}
            onChange={(e) => setEndMonth(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            sx={{ minWidth: 150 }}
          />
        ) : (
          <Button size="small" variant="text" onClick={() => setShowEndMonth(true)}>+ 終点を設定</Button>
        )}
        <Button variant="contained" onClick={handleSearch}>
          絞り込み
        </Button>
        <Button variant="text" onClick={handleClearFilter}>
          クリア
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={() => { resetForm(); setOpen(true); }}>
          新規案件を追加
        </Button>
      </Box>

      <MonthSummaryBar type="projects" month={baseMonth} />

      <DataGrid
        rows={projects}
        columns={columns}
        autoHeight
        pageSize={10}
        disableSelectionOnClick
        getRowHeight={() => 100}
      />

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>案件をコピー</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            コピー先の月
          </Typography>
          <RadioGroup
            value={copyMode}
            onChange={(e) => setCopyMode(e.target.value)}
          >
            <FormControlLabel value="this" control={<Radio />} label="当月分としてコピー" />
            <FormControlLabel value="next" control={<Radio />} label="翌月分としてコピー" />
            <FormControlLabel value="custom" control={<Radio />} label="その他（月を選択）" />
          </RadioGroup>

          {copyMode === 'custom' && (
            <TextField
              fullWidth
              margin="dense"
              type="month"
              label="コピー先の月"
              value={customMonth}
              onChange={(e) => setCustomMonth(e.target.value)}
            />
          )}

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            一緒にコピーする項目
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={copyMembers}
                onChange={(e) => setCopyMembers(e.target.checked)}
              />
            }
            label="メンバー（役割）"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={copyPayments}
                onChange={(e) => setCopyPayments(e.target.checked)}
              />
            }
            label="外注費（案件原価）※支払ステータスは承認待ちに戻ります"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={copyContractMinutes}
                onChange={(e) => setCopyContractMinutes(e.target.checked)}
              />
            }
            label="契約工数（外すとリセットされます）"
          />

          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            引き継ぐ項目：案件名 / クライアント / 代理店 / 種類 / 運用区分 / 確度 / 担当リーダー / 案件金額 / 備考
            <br />
            リセットされる項目：ステータス（未着手）/ 進捗（0%）/ 工数実績 / 発行済の見積・請求・納品・受注 / 契約
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copying}>キャンセル</Button>
          <Button variant="contained" onClick={handleCopyExecute} disabled={copying}>
            {copying ? 'コピー中...' : 'コピー'}
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={!!copyResult}
        autoHideDuration={8000}
        onClose={() => setCopyResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={copyResult?.severity || 'info'}
          onClose={() => setCopyResult(null)}
          sx={{ maxWidth: 720 }}
        >
          {copyResult?.message}
        </Alert>
      </Snackbar>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editId ? '案件を編集' : '新規案件を登録'}</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1, width: '100%' }}>

            {/* 1行目：案件名 / クライアント */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    label="案件名"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={6} sx={{ minWidth: 0 }}>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={customers}
                    getOptionLabel={(o) => o.name || ''}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    value={selectedCustomer}
                    onChange={(e, v) => {
                      setSelectedCustomer(v);
                      setForm({ ...form, customerId: v ? v.id : '' });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="クライアント" />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* 2行目：代理店 / 種類 / 確度 */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={4} sx={{ minWidth: 0 }}>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={customers}
                    getOptionLabel={(o) => o.name || ''}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    value={agencyValue}
                    onChange={(e, v) =>
                      setForm({ ...form, agencyCustomerId: v ? v.id : '' })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="代理店（任意）" />
                    )}
                  />
                </Grid>

                <Grid size={4}>
                  <TextField
                    select
                    fullWidth
                    label="種類"
                    name="projectType"
                    value={form.projectType}
                    onChange={handleChange}
                  >
                    {PROJECT_TYPES.map(t => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* 運用案件のときだけ「繰り返し」セレクタを表示 (xlsx 行12) */}
                {form.projectType === 'operation' && (
                  <Grid size={4}>
                    <TextField
                      select
                      fullWidth
                      label="繰り返し"
                      name="recurringType"
                      value={form.recurringType}
                      onChange={handleChange}
                    >
                      <MenuItem value="">なし</MenuItem>
                      <MenuItem value="monthly">毎月</MenuItem>
                      <MenuItem value="yearly">毎年</MenuItem>
                    </TextField>
                  </Grid>
                )}

                <Grid size={4}>
                  <TextField
                    select
                    fullWidth
                    label="確度"
                    name="probability"
                    value={form.probability}
                    onChange={handleChange}
                  >
                    {PROBABILITIES.map(p => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>

            {/* 3行目：ステータス / 担当者 */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    select
                    fullWidth
                    label="ステータス"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    {STATUSES.map(s => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={6} sx={{ minWidth: 0 }}>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={users}
                    getOptionLabel={(o) => o.name || ''}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                    value={selectedLeader}
                    onChange={(e, v) => {
                      setSelectedLeader(v);
                      setForm({ ...form, leaderUserId: v ? v.id : '' });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="担当者" />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* 4行目：開始日 / 終了日 */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="開始日"
                    name="startDate"
                    InputLabelProps={{ shrink: true }}
                    value={form.startDate}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="終了日"
                    name="endDate"
                    InputLabelProps={{ shrink: true }}
                    value={form.endDate}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* 5行目：金額 / 契約工数 */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="金額"
                    name="totalCost"
                    value={form.totalCost}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="契約工数（分）"
                    name="contractMinutes"
                    value={form.contractMinutes}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* 説明（1項目1行） */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="説明"
                name="description"
                multiline
                minRows={3}
                maxRows={6}
                value={form.description}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
                    height: 'auto',
                    alignItems: 'flex-start',
                  },
                }}
              />

            </Grid>

            {/* 進捗 */}
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="進捗 (%)"
                name="progress"
                value={form.progress}
                onChange={handleChange}
              />
            </Grid>

          </Grid>
        </DialogContent>


        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editId ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!memberProjectId}
        onClose={() => setMemberProjectId(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>プロジェクトメンバー管理</DialogTitle>
        <DialogContent>
          {memberProjectId && <ProjectMemberList projectId={memberProjectId} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberProjectId(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
